import React, {ReactElement, useCallback, useContext, useMemo} from 'react'
// import {useDebouncedCallback} from 'use-debounce'
import {OrgScopedEvent} from '../../common/types/workbench-analytics'
import {Analytics} from './analytics'

type ActionFlowAnalytics = {
  track: (event: ActionFlowTrackEvent, immediate?: boolean) => Promise<void>
}

const ActionFlowAnalyticsContext = React.createContext<
  ActionFlowAnalytics | undefined
>(undefined)

type ActionFlowAnalyticsProps = {
  organisationId: string
  environmentId: string
  children: ReactElement
  analytics: Analytics
  actionFlow: {
    id: string
    version: number
    name?: string
  }
}

type ActionFlowTrackEvent = {
  event: OrgScopedEvent['event']
  messageId?: string
  properties?: Partial<Omit<OrgScopedEvent['properties'], 'email'>> // looser of the analytics type
}

/** provider that wraps an action flow context to avoid have to do on each track event */
export function ActionFlowAnalyticsProvider({
  organisationId,
  environmentId,
  actionFlow,
  analytics,
  children
}: ActionFlowAnalyticsProps) {
  const trackFn = useCallback(
    async (event: ActionFlowTrackEvent, immediate?: boolean) => {
      return analytics.track(
        {
          ...event,
          properties: {
            category: 'action-flows',
            environment_id: environmentId,
            org_id: organisationId,
            action_flow_id: actionFlow.id,
            action_flow_version: actionFlow.version,
            action_flow_name: actionFlow.name,
            ...event.properties // override any of the above
          }
        },
        immediate
      )
    },
    [
      actionFlow.id,
      actionFlow.version,
      actionFlow.name,
      environmentId,
      organisationId,
      analytics
    ]
  )

  const wrapped = useMemo(() => {
    return {track: trackFn}
  }, [trackFn])

  return (
    <ActionFlowAnalyticsContext.Provider value={wrapped}>
      {children}
    </ActionFlowAnalyticsContext.Provider>
  )
}

export function useActionFlowAnalytics() {
  const analyticsCtx = useContext(ActionFlowAnalyticsContext)

  if (!analyticsCtx) {
    throw new Error(
      'Calling useAnalytics outside of ActionFlowAnalyticsProvider'
    )
  }

  return analyticsCtx
}
